import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAccountSelectionContext } from './AccountSelectionContext';
import { telemetry } from '../../telemetry';

/**
 * Bridges AccountSelectionContext (which can't reach useAuth0 because
 * Auth0Provider is its descendant in the tree) with the Auth0 SDK.
 *
 * When the URL points at an account the user has access to but is not the one
 * currently in their session, AccountSelectionProvider sets a `pendingAccountSwitch`
 * signal. This component watches for it and triggers a real /authorize redirect
 * with `organization` in `authorizationParams`, which is the only way to mint a
 * new access token whose `org_id` claim matches the chosen organization.
 * Refresh-token grant cannot establish org_id on a session that started without
 * one. See AUTHENTICATION.md §6.
 */
export const AccountAutoSwitchEffect = () => {
  const { pendingAccountSwitch, clearPendingAccountSwitch } = useAccountSelectionContext();
  const { loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!pendingAccountSwitch) return;
    const { authZeroOrgId, returnTo } = pendingAccountSwitch;
    clearPendingAccountSwitch();
    loginWithRedirect({
      authorizationParams: { organization: authZeroOrgId },
      appState: { returnTo },
    }).catch((error) => {
      telemetry.captureError(error, {
        message: 'Failed to redirect for account auto-switch',
        authZeroOrgId,
      });
    });
  }, [pendingAccountSwitch, loginWithRedirect, clearPendingAccountSwitch]);

  return null;
};
